import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { Menu } from '../Icons';
import type { DropDownMenuProps } from './types';
import './dropDownMenu.scss';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {};

export const DropDownMenu: React.FC<DropDownMenuProps> = ({
  className,
  children,
  defaultOpen,
  scrollable = false,
  selectedItemRef,
  triggerElement = <Menu onClick={noop} />,
  portal = false,
  ...props
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const toggleOpen = useCallback(() => setOpen(o => !o), []);
  const closeMenu = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (ref.current && !ref.current.contains(target) && !optionsRef.current?.contains(target)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [closeMenu]);

  useLayoutEffect(() => {
    if (!open || !portal) return undefined;

    const updatePosition = () => {
      if (!ref.current || !optionsRef.current) return;

      const triggerRect = ref.current.getBoundingClientRect();
      const { width: menuWidth, height: menuHeight } = optionsRef.current.getBoundingClientRect();
      const gap = 7;
      const viewportMargin = 8;
      const maxTop = window.innerHeight - menuHeight - viewportMargin;
      const maxLeft = window.innerWidth - menuWidth - viewportMargin;

      // Prefer below the trigger; open above if the menu would cross the bottom edge.
      let menuTop = triggerRect.bottom + gap;
      if (menuTop > maxTop) {
        menuTop = triggerRect.top - gap - menuHeight;
      }

      // Keep the initial position within the viewport, then convert to document coordinates.
      setPosition({
        top: Math.max(viewportMargin, Math.min(menuTop, maxTop)) + window.scrollY,
        left: Math.max(viewportMargin, Math.min(triggerRect.left, maxLeft)) + window.scrollX,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, portal, children]);

  useEffect(() => {
    const options = optionsRef.current;
    if (open && scrollable && options && options.scrollHeight > options.clientHeight)
      selectedItemRef?.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
  }, [open, scrollable, selectedItemRef]);

  const options = (
    <ul ref={optionsRef} className={cn({ 'dropdown-default__options--scrollable': scrollable })}>
      {children}
    </ul>
  );

  return (
    <div role="none" ref={ref} className={cn('dropdown-default', { open }, className)} onClick={toggleOpen} {...props}>
      {triggerElement}
      {portal
        ? open &&
          createPortal(
            <div className="dropdown-default open dropdown-default--portal" style={position}>
              {options}
            </div>,
            document.body
          )
        : options}
    </div>
  );
};
